import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatHeat } from "@/utils/format";

interface ArticleCardProps {
  article: Article;
  variant?: "vertical" | "horizontal" | "compact";
  showCategory?: boolean;
}

export default function ArticleCard({
  article,
  variant = "vertical",
  showCategory = true,
}: ArticleCardProps) {
  const categoryTag = (
    <span className="inline-flex items-center rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
      {article.category}
    </span>
  );

  const heatTag = (
    <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
      <span className="flex h-3.5 w-3.5 items-center justify-center">
        <i className="ri-fire-line text-[12px] text-primary-500"></i>
      </span>
      {formatHeat(article.heat)}
    </span>
  );

  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 rounded-lg px-2 py-2.5 transition-colors duration-200 hover:bg-primary-50/70"
      >
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
          <i className="ri-file-text-line text-base text-primary-500"></i>
        </span>
        <span className="flex-1">
          <span className="block text-sm font-medium leading-snug text-foreground-900 transition-colors duration-200 group-hover:text-primary-700">
            {article.title}
          </span>
          <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
            {showCategory && categoryTag}
            {heatTag}
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex flex-col gap-4 rounded-card border border-background-200/80 bg-background-50 p-3 transition-colors duration-200 hover:border-primary-200 sm:flex-row"
      >
        <div className="h-40 w-full shrink-0 overflow-hidden rounded-xl bg-background-200 sm:h-28 sm:w-44">
          <img
            src={article.cover}
            alt={article.coverAlt}
            title={`${article.title}`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              {showCategory && categoryTag}
              <span className="text-xs text-foreground-500">{article.date}</span>
            </div>
            <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors duration-200 group-hover:text-primary-700">
              {article.title}
            </h3>
            <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
              {article.excerpt}
            </p>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-foreground-500">
              {article.author} · {article.readTime}
            </span>
            {heatTag}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-card border border-background-200/80 bg-background-50 transition-colors duration-200 hover:border-primary-200"
    >
      <div className="h-48 w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.coverAlt}
          title={article.title}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2">
          {showCategory && categoryTag}
          <span className="text-xs text-foreground-500">{article.date}</span>
        </div>
        <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors duration-200 group-hover:text-primary-700">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3">
          <span className="text-xs text-foreground-500">{article.author}</span>
          {heatTag}
        </div>
      </div>
    </Link>
  );
}